/** Business-friendly behaviour catalog for RA Bill Template designer. */

export const BEHAVIOUR_TYPES = {
  MANUAL_ENTRY: 'MANUAL_ENTRY',
  AUTO_LOOKUP: 'AUTO_LOOKUP',
  PREVIOUS_RA_VALUE: 'PREVIOUS_RA_VALUE',
  SUM_OF_FIELDS: 'SUM_OF_FIELDS',
  SUBTRACT_FIELDS: 'SUBTRACT_FIELDS',
  PERCENTAGE: 'PERCENTAGE',
  CURRENT_MINUS_PREVIOUS: 'CURRENT_MINUS_PREVIOUS',
  INCREMENTAL_VALUE: 'INCREMENTAL_VALUE',
  CUMULATIVE_VALUE: 'CUMULATIVE_VALUE',
  QUANTITY_RATE: 'QUANTITY_RATE',
  CAPPED_PERCENTAGE: 'CAPPED_PERCENTAGE',
  BALANCE_RECOVERY: 'BALANCE_RECOVERY',
  CONDITIONAL: 'CONDITIONAL',
  WITHHELD: 'WITHHELD',
  RELEASE: 'RELEASE',
  AUTO_TOTAL: 'AUTO_TOTAL',
  REFERENCE_ONLY: 'REFERENCE_ONLY',
  CUSTOM_FORMULA: 'CUSTOM_FORMULA',
};

export const BEHAVIOUR_OPTIONS = [
  { value: BEHAVIOUR_TYPES.MANUAL_ENTRY, label: 'Manual Entry' },
  { value: BEHAVIOUR_TYPES.AUTO_LOOKUP, label: 'Auto Lookup' },
  { value: BEHAVIOUR_TYPES.PREVIOUS_RA_VALUE, label: 'Previous RA Value' },
  { value: BEHAVIOUR_TYPES.SUM_OF_FIELDS, label: 'Sum of Fields' },
  { value: BEHAVIOUR_TYPES.SUBTRACT_FIELDS, label: 'Subtract Fields' },
  { value: BEHAVIOUR_TYPES.PERCENTAGE, label: 'Percentage' },
  { value: BEHAVIOUR_TYPES.CURRENT_MINUS_PREVIOUS, label: 'Current Minus Previous' },
  { value: BEHAVIOUR_TYPES.INCREMENTAL_VALUE, label: 'Incremental Value' },
  { value: BEHAVIOUR_TYPES.CUMULATIVE_VALUE, label: 'Cumulative Value' },
  { value: BEHAVIOUR_TYPES.QUANTITY_RATE, label: 'Quantity × Rate' },
  { value: BEHAVIOUR_TYPES.CAPPED_PERCENTAGE, label: 'Percentage with Maximum Limit' },
  { value: BEHAVIOUR_TYPES.BALANCE_RECOVERY, label: 'Recover from Outstanding Balance' },
  { value: BEHAVIOUR_TYPES.CONDITIONAL, label: 'Conditional Calculation' },
  { value: BEHAVIOUR_TYPES.WITHHELD, label: 'Withheld' },
  { value: BEHAVIOUR_TYPES.RELEASE, label: 'Release' },
  { value: BEHAVIOUR_TYPES.AUTO_TOTAL, label: 'Total' },
  { value: BEHAVIOUR_TYPES.REFERENCE_ONLY, label: 'Reference / Information Only' },
  { value: BEHAVIOUR_TYPES.CUSTOM_FORMULA, label: 'Custom Formula' },
];

export const FINANCIAL_CLASSIFICATIONS = [
  { value: 'WORK_VALUE', label: 'Work Value' },
  { value: 'ADVANCE', label: 'Advance' },
  { value: 'PRICE_ADJUSTMENT', label: 'Price Adjustment' },
  { value: 'STATUTORY_RECOVERY', label: 'Statutory Recovery' },
  { value: 'CONTRACTUAL_RECOVERY', label: 'Contractual Recovery' },
  { value: 'SECURITY_DEPOSIT', label: 'Security Deposit' },
  { value: 'RETENTION', label: 'Retention' },
  { value: 'CLIENT_MATERIAL_RECOVERY', label: 'Client Material Recovery' },
  { value: 'WATER_RECOVERY', label: 'Water Recovery' },
  { value: 'ELECTRICITY_RECOVERY', label: 'Electricity Recovery' },
  { value: 'ADVANCE_RECOVERY', label: 'Advance Recovery' },
  { value: 'PENALTY', label: 'Penalty' },
  { value: 'WITHHELD', label: 'Withheld' },
  { value: 'RELEASE', label: 'Release' },
  { value: 'CERTIFICATION_TOTAL', label: 'Certification Total' },
  { value: 'PAYMENT', label: 'Payment' },
  { value: 'REFERENCE', label: 'Reference / Information' },
];

export const FINANCIAL_EFFECTS = [
  { value: 'INFORMATION', label: 'Information Only' },
  { value: 'ADD', label: 'Add' },
  { value: 'DEDUCT', label: 'Deduct' },
  { value: 'BALANCE', label: 'Balance / Carry Forward' },
];

export const INCLUDE_IN_GROUPS = [
  { value: 'NONE', label: 'None' },
  { value: 'GROSS_VALUE', label: 'Gross Value' },
  { value: 'TOTAL_RECOVERIES', label: 'Total Recoveries' },
  { value: 'TOTAL_WITHHELD', label: 'Total Withheld' },
  { value: 'TOTAL_DEDUCTIONS', label: 'Total Deductions' },
  { value: 'NET_PAYABLE', label: 'Net Payable' },
  { value: 'CUSTOM_TOTAL', label: 'Custom Total' },
];

export const PREVIOUS_BEHAVIOURS = [
  {
    value: 'SAME_FIELD',
    label: 'From previous RA bill — this same field (RA-2 onwards)',
    shortLabel: 'Previous RA bill (same field)',
  },
  {
    value: 'OTHER_FIELD',
    label: 'From previous RA bill — a different field',
    shortLabel: 'Previous RA bill (other field)',
  },
  {
    value: 'ALWAYS_ZERO',
    label: 'Always 0 (no previous carry-forward)',
    shortLabel: 'Always 0',
  },
  {
    value: 'NOT_APPLICABLE',
    label: 'Not used in Previous column',
    shortLabel: 'Not applicable',
  },
  {
    value: 'CUSTOM',
    label: 'Custom previous-value formula',
    shortLabel: 'Custom',
  },
];

export const labelForPreviousBehaviour = (value) => {
  const hit = PREVIOUS_BEHAVIOURS.find((o) => o.value === String(value || 'SAME_FIELD').toUpperCase());
  return hit?.shortLabel || hit?.label || String(value || 'SAME_FIELD');
};

export const COMPARISON_BEHAVIOURS = [
  { value: 'CURRENT_MINUS_PREVIOUS', label: 'Current − Previous' },
  { value: 'CURRENT_ONLY', label: 'Current Only' },
  { value: 'PREVIOUS_ONLY', label: 'Previous Only' },
  { value: 'NO_COMPARISON', label: 'No Comparison' },
  { value: 'CUSTOM', label: 'Custom' },
];

export const RATE_SOURCES = [
  { value: 'FIXED', label: 'Fixed Rate' },
  { value: 'DEPARTMENT', label: 'Department Rule' },
  { value: 'PROJECT', label: 'Project' },
  { value: 'AGREEMENT', label: 'Agreement' },
  { value: 'STATUTORY', label: 'Statutory' },
  { value: 'MANUAL', label: 'Manual' },
];

export const ROUNDING_OPTIONS = [
  { value: 'ROUND', label: 'Nearest Rupee' },
  { value: 'FLOOR', label: 'Round Down' },
  { value: 'CEIL', label: 'Round Up' },
  { value: 'NONE', label: 'No Rounding' },
];

export const SEMANTIC_ROLE_OPTIONS = [
  { value: 'GROSS_UPTO_DATE', label: 'Gross Upto Date' },
  { value: 'CURRENT_RA_VALUE', label: 'Current RA Value' },
  { value: 'NET_EXCL_SECURED_ADVANCE', label: 'Net Excluding Secured Advance' },
  { value: 'TOTAL_RECOVERIES', label: 'Total Recoveries' },
  { value: 'TOTAL_WITHHELD', label: 'Total Withheld' },
  { value: 'TOTAL_DEDUCTIONS', label: 'Total Deductions' },
  { value: 'NET_PAYABLE', label: 'Net Payable' },
  { value: 'PROGRESS_PERCENTAGE', label: 'Progress %' },
  { value: 'SECURITY_DEPOSIT', label: 'Security Deposit' },
  { value: 'GST_DEDUCTION', label: 'GST Deduction' },
  { value: 'INCOME_TAX', label: 'Income Tax' },
  { value: 'LABOUR_CESS', label: 'Labour Cess' },
  { value: 'PREVIOUS_RA_GROSS', label: 'Previous RA Gross' },
  { value: 'SECURED_ADVANCE_ADDED', label: 'Secured Advance Added' },
];

export const DATA_TYPE_OPTIONS = [
  { value: 'AMOUNT', label: 'Amount' },
  { value: 'NUMBER', label: 'Number' },
  { value: 'PERCENTAGE', label: 'Percentage' },
  { value: 'TEXT', label: 'Text' },
  { value: 'DATE', label: 'Date' },
  { value: 'BOOLEAN', label: 'Yes / No' },
];

export const SECTION_TYPE_OPTIONS = [
  { value: 'HEADER', label: 'Bill Header' },
  { value: 'WORK_VALUE', label: 'Work Done & Bill Value' },
  { value: 'ADVANCES', label: 'Advances' },
  { value: 'CURRENT_CERTIFICATE', label: 'Current Certificate' },
  { value: 'RECOVERIES', label: 'Recoveries' },
  { value: 'WITHHELD', label: 'Withheld' },
  { value: 'NET_CERTIFICATION', label: 'Bill Totals' },
  { value: 'CUSTOM', label: 'Custom Section' },
];

/** Map behaviour → fieldType / sourceType defaults. */
export const behaviourToFieldMeta = (behaviourType) => {
  switch (behaviourType) {
    case BEHAVIOUR_TYPES.MANUAL_ENTRY:
    case BEHAVIOUR_TYPES.REFERENCE_ONLY:
    case BEHAVIOUR_TYPES.WITHHELD:
    case BEHAVIOUR_TYPES.RELEASE:
      return { fieldType: 'INPUT', sourceType: 'MANUAL' };
    case BEHAVIOUR_TYPES.AUTO_LOOKUP:
      return { fieldType: 'LOOKUP', sourceType: 'SYSTEM' };
    case BEHAVIOUR_TYPES.PREVIOUS_RA_VALUE:
      return { fieldType: 'PREVIOUS_RA', sourceType: 'PREVIOUS_RA' };
    case BEHAVIOUR_TYPES.CUSTOM_FORMULA:
    case BEHAVIOUR_TYPES.SUM_OF_FIELDS:
    case BEHAVIOUR_TYPES.SUBTRACT_FIELDS:
    case BEHAVIOUR_TYPES.PERCENTAGE:
    case BEHAVIOUR_TYPES.CURRENT_MINUS_PREVIOUS:
    case BEHAVIOUR_TYPES.INCREMENTAL_VALUE:
    case BEHAVIOUR_TYPES.CUMULATIVE_VALUE:
    case BEHAVIOUR_TYPES.QUANTITY_RATE:
    case BEHAVIOUR_TYPES.CAPPED_PERCENTAGE:
    case BEHAVIOUR_TYPES.BALANCE_RECOVERY:
    case BEHAVIOUR_TYPES.CONDITIONAL:
    case BEHAVIOUR_TYPES.AUTO_TOTAL:
      return { fieldType: 'FORMULA', sourceType: 'FORMULA' };
    default:
      return { fieldType: 'INPUT', sourceType: 'MANUAL' };
  }
};

export const defaultBusinessConfig = (overrides = {}) => ({
  behaviourType: BEHAVIOUR_TYPES.MANUAL_ENTRY,
  financialClassification: 'REFERENCE',
  financialEffect: 'INFORMATION',
  includeInGroup: 'NONE',
  rateSource: 'FIXED',
  rateValue: null,
  baseFieldCode: null,
  previousBehaviour: 'SAME_FIELD',
  previousFieldCode: null,
  comparisonBehaviour: 'CURRENT_MINUS_PREVIOUS',
  lineReference: '',
  capType: null,
  capValue: null,
  capBaseFieldCode: null,
  sumFieldCodes: [],
  subtractFieldCodes: [],
  quantityFieldCode: null,
  rateFieldCode: null,
  originalAdvanceFieldCode: null,
  previousOutstandingFieldCode: null,
  recoveryMethod: 'PERCENTAGE',
  capToOutstanding: true,
  conditionTree: null,
  elseValue: 0,
  formulaBlocks: null,
  uiFormulaVersion: 1,
  ...overrides,
});

export const getBusinessConfig = (field) => {
  const raw = field?.metadata?.businessConfig;
  if (raw && typeof raw === 'object') {
    return { ...defaultBusinessConfig(), ...raw };
  }
  return null;
};

export const setBusinessConfigOnField = (field, businessConfig) => ({
  ...field,
  metadata: {
    ...(field.metadata && typeof field.metadata === 'object' ? field.metadata : {}),
    businessConfig: { ...defaultBusinessConfig(), ...businessConfig },
  },
});

export const labelForBehaviour = (value) =>
  BEHAVIOUR_OPTIONS.find((o) => o.value === value)?.label || value || '—';

export const labelForSemanticRole = (value) =>
  SEMANTIC_ROLE_OPTIONS.find((o) => o.value === value)?.label || value || '—';
